import { TPatientInfo } from "../../../../../services/api-clinic";
import {
  TCommunicationsAssignedPhoneNumberItem,
  TCommunicationsPhoneNumberInventoryItem,
  TCommunicationsConversation,
  TCommunicationsCallNote,
  TCommunicationsPhoneNumberPricing,
  TCommunicationsRecording,
  TDocumentationPatientNote,
  TGetCommunicationsUsageResponse,
} from "../../../../../services/api-communications";
import type { VoiceNote } from "../../consults/voice-notes/types/VoiceNotes";
import { TMessageConversationListItem, TPatientOption } from "../types";

export const parseApiList = <T>(payload: unknown): T[] => {
  if (Array.isArray(payload)) {
    return payload as T[];
  }

  if (!payload || typeof payload !== "object") {
    return [];
  }

  const objectPayload = payload as Record<string, unknown>;
  if (Array.isArray(objectPayload.results)) {
    return objectPayload.results as T[];
  }
  if (Array.isArray(objectPayload.phone_numbers)) {
    return objectPayload.phone_numbers as T[];
  }
  if (Array.isArray(objectPayload.items)) {
    return objectPayload.items as T[];
  }
  return [];
};

const parsePhoneNumberId = (value: unknown): number | null => {
  const parsedValue =
    typeof value === "string" ? Number(value.trim()) : Number(value);
  if (!Number.isFinite(parsedValue)) {
    return null;
  }
  return Math.trunc(parsedValue);
};

const parsePhoneNumberCapabilities = (
  value: unknown,
): string[] | Record<string, boolean> | null => {
  if (!value) {
    return null;
  }
  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === "string");
  }
  if (typeof value === "object") {
    return value as Record<string, boolean>;
  }
  return null;
};

const parseOptionalString = (value: unknown): string | null => {
  if (typeof value !== "string") {
    return null;
  }
  return value.trim() || null;
};

const parseRawPhoneNumberId = (candidate: Record<string, unknown>): number => {
  const nestedPhoneNumber =
    candidate.phone_number && typeof candidate.phone_number === "object"
      ? (candidate.phone_number as Record<string, unknown>)
      : null;

  const resolvedId =
    parsePhoneNumberId(candidate.phone_number_id) ??
    parsePhoneNumberId(candidate.phoneNumberId) ??
    parsePhoneNumberId(candidate.id) ??
    parsePhoneNumberId(nestedPhoneNumber?.phone_number_id) ??
    parsePhoneNumberId(nestedPhoneNumber?.id);

  if (resolvedId === null) {
    return NaN;
  }

  return resolvedId;
};

const parseRawE164 = (candidate: Record<string, unknown>): string => {
  const nestedPhoneNumber =
    candidate.phone_number && typeof candidate.phone_number === "object"
      ? (candidate.phone_number as Record<string, unknown>)
      : null;

  return (
    parseOptionalString(candidate.e164) ||
    parseOptionalString(candidate.phone_number_e164) ||
    parseOptionalString(nestedPhoneNumber?.e164) ||
    ""
  );
};

export const parseAssignedPhoneNumbersPayload = (
  payload: unknown,
): TCommunicationsAssignedPhoneNumberItem[] => {
  const results: TCommunicationsAssignedPhoneNumberItem[] = [];

  parseApiList<Record<string, unknown>>(payload).forEach((candidate) => {
    const phoneNumberId = parseRawPhoneNumberId(candidate);
    if (!Number.isFinite(phoneNumberId)) {
      return;
    }

    results.push({
      phone_number_id: phoneNumberId,
      e164: parseRawE164(candidate),
      friendly_name: parseOptionalString(candidate.friendly_name) || undefined,
      capabilities: parsePhoneNumberCapabilities(candidate.capabilities),
      status: parseOptionalString(candidate.status) || undefined,
      is_primary:
        typeof candidate.is_primary === "boolean"
          ? candidate.is_primary
          : undefined,
      assigned_at: parseOptionalString(candidate.assigned_at) || undefined,
      monthly_price_cents: Number.isFinite(
        Number(candidate.monthly_price_cents),
      )
        ? Number(candidate.monthly_price_cents)
        : null,
      currency: parseOptionalString(candidate.currency) || undefined,
    });
  });

  return results;
};

export const parseAvailablePhoneNumbersPayload = (
  payload: unknown,
): TCommunicationsPhoneNumberInventoryItem[] => {
  const results: TCommunicationsPhoneNumberInventoryItem[] = [];

  parseApiList<Record<string, unknown>>(payload).forEach((candidate) => {
    const phoneNumberId = parseRawPhoneNumberId(candidate);
    if (!Number.isFinite(phoneNumberId)) {
      return;
    }

    results.push({
      phone_number_id: phoneNumberId,
      e164: parseRawE164(candidate),
      friendly_name: parseOptionalString(candidate.friendly_name) || undefined,
      capabilities: parsePhoneNumberCapabilities(candidate.capabilities),
      status: parseOptionalString(candidate.status) || undefined,
      plan_code: parseOptionalString(candidate.plan_code) || undefined,
      monthly_price_cents: Number.isFinite(
        Number(candidate.monthly_price_cents),
      )
        ? Number(candidate.monthly_price_cents)
        : null,
      currency: parseOptionalString(candidate.currency) || undefined,
      display_label: parseOptionalString(candidate.display_label) || undefined,
    });
  });

  return results;
};

export const parsePricingPayload = (
  payload: unknown,
): TCommunicationsPhoneNumberPricing | null => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const objectPayload = payload as Record<string, unknown>;
  const pricingCandidate =
    objectPayload.pricing && typeof objectPayload.pricing === "object"
      ? (objectPayload.pricing as Record<string, unknown>)
      : objectPayload;

  const monthlyPriceCents = Number(pricingCandidate.monthly_price_cents);
  const includedNumberCount = Number(pricingCandidate.included_number_count);
  const includedMinutes = Number(pricingCandidate.included_minutes);
  const extraNumberMonthlyPriceCents = Number(
    pricingCandidate.extra_number_monthly_price_cents,
  );
  const overageMinutePriceCents = Number(
    pricingCandidate.overage_minute_price_cents,
  );

  if (
    !Number.isFinite(monthlyPriceCents) ||
    !Number.isFinite(includedNumberCount) ||
    !Number.isFinite(includedMinutes) ||
    !Number.isFinite(extraNumberMonthlyPriceCents) ||
    !Number.isFinite(overageMinutePriceCents)
  ) {
    return null;
  }

  return {
    monthly_price_cents: monthlyPriceCents,
    currency:
      String(pricingCandidate.currency || "USD")
        .trim()
        .toUpperCase() || "USD",
    included_number_count: includedNumberCount,
    included_minutes: includedMinutes,
    extra_number_monthly_price_cents: extraNumberMonthlyPriceCents,
    overage_minute_price_cents: overageMinutePriceCents,
    plan_code:
      pricingCandidate.plan_code === null ||
      typeof pricingCandidate.plan_code === "string"
        ? (pricingCandidate.plan_code as string | null)
        : null,
    setup_price_cents:
      pricingCandidate.setup_price_cents === null ||
      pricingCandidate.setup_price_cents === undefined
        ? null
        : Number(pricingCandidate.setup_price_cents),
    display_label:
      pricingCandidate.display_label === null ||
      typeof pricingCandidate.display_label === "string"
        ? (pricingCandidate.display_label as string | null)
        : null,
  };
};

export const parseUsagePayload = (
  payload: unknown,
): TGetCommunicationsUsageResponse | null => {
  if (!payload || typeof payload !== "object") {
    return null;
  }

  const objectPayload = payload as Record<string, unknown>;
  const usageCandidate =
    objectPayload.usage && typeof objectPayload.usage === "object"
      ? (objectPayload.usage as Record<string, unknown>)
      : objectPayload;

  const totalCalls = Number(usageCandidate.total_calls);
  const completedCalls = Number(usageCandidate.completed_calls);
  const totalDurationSeconds = Number(usageCandidate.total_duration_seconds);
  const totalBillableMinutes = Number(usageCandidate.total_billable_minutes);

  if (
    !Number.isFinite(totalCalls) ||
    !Number.isFinite(completedCalls) ||
    !Number.isFinite(totalDurationSeconds) ||
    !Number.isFinite(totalBillableMinutes)
  ) {
    return null;
  }

  const bucketPayload = parseApiList<Record<string, unknown>>(
    usageCandidate.buckets,
  );

  return {
    total_calls: totalCalls,
    completed_calls: completedCalls,
    total_duration_seconds: totalDurationSeconds,
    total_billable_minutes: totalBillableMinutes,
    included_minutes:
      usageCandidate.included_minutes === undefined ||
      usageCandidate.included_minutes === null
        ? undefined
        : Number(usageCandidate.included_minutes),
    overage_minutes:
      usageCandidate.overage_minutes === undefined ||
      usageCandidate.overage_minutes === null
        ? undefined
        : Number(usageCandidate.overage_minutes),
    period_start:
      typeof usageCandidate.period_start === "string"
        ? usageCandidate.period_start
        : null,
    period_end:
      typeof usageCandidate.period_end === "string"
        ? usageCandidate.period_end
        : null,
    buckets: bucketPayload.map((bucket) => ({
      period_start: String(bucket.period_start || ""),
      period_end: String(bucket.period_end || ""),
      total_calls: Number(bucket.total_calls || 0),
      completed_calls: Number(bucket.completed_calls || 0),
      total_duration_seconds: Number(bucket.total_duration_seconds || 0),
      total_billable_minutes: Number(bucket.total_billable_minutes || 0),
      ...(bucket.included_minutes !== undefined
        ? {
            included_minutes: Number(bucket.included_minutes || 0),
          }
        : {}),
      ...(bucket.overage_minutes !== undefined
        ? {
            overage_minutes: Number(bucket.overage_minutes || 0),
          }
        : {}),
    })),
  };
};

export const getUrlPath = (value?: string | null): string => {
  const trimmedValue = (value || "").trim();
  if (!trimmedValue) {
    return "";
  }

  try {
    return new URL(trimmedValue).pathname;
  } catch (error) {
    return "";
  }
};

export const getPathBasename = (value: string): string => {
  const normalizedValue = (value || "").trim();
  if (!normalizedValue) {
    return "";
  }

  const segments = normalizedValue.split("/").filter(Boolean);
  return segments[segments.length - 1] || "";
};

export const recordingMatchesAudioSource = (
  recording: TCommunicationsRecording,
  source: { s3Key?: string | null; getUrl?: string | null },
): boolean => {
  const recordingS3Key = (recording.s3_key || "").trim();
  const sourceS3Key = (source.s3Key || "").trim();
  if (recordingS3Key && sourceS3Key && recordingS3Key === sourceS3Key) {
    return true;
  }

  const recordingPath = getUrlPath(recording.recording_url);
  const sourcePath = getUrlPath(source.getUrl);
  if (recordingPath && sourcePath && recordingPath === sourcePath) {
    return true;
  }

  const recordingBasename =
    getPathBasename(recordingS3Key) || getPathBasename(recordingPath);
  const sourceBasename =
    getPathBasename(sourceS3Key) || getPathBasename(sourcePath);

  return (
    !!recordingBasename &&
    !!sourceBasename &&
    recordingBasename === sourceBasename
  );
};

export const toPatientOption = (patient: TPatientInfo): TPatientOption => ({
  uuid: patient.uuid,
  displayName:
    `${patient.firstName || ""} ${patient.lastName || ""}`.trim() ||
    patient.email ||
    patient.phone ||
    patient.uuid,
  firstName: patient.firstName || "",
  lastName: patient.lastName || "",
  email: patient.email || "",
  phone: patient.phone || "",
});

export const mergePatientOptions = (
  existingPatients: TPatientOption[],
  nextPatients: TPatientOption[],
): TPatientOption[] => {
  if (!nextPatients.length) {
    return existingPatients;
  }

  const mergedByUuid = new Map<string, TPatientOption>();
  let changed = false;

  existingPatients.forEach((patient) => {
    mergedByUuid.set(patient.uuid, patient);
  });

  nextPatients.forEach((patient) => {
    const currentPatient = mergedByUuid.get(patient.uuid);
    if (
      !currentPatient ||
      currentPatient.displayName !== patient.displayName ||
      currentPatient.firstName !== patient.firstName ||
      currentPatient.lastName !== patient.lastName ||
      currentPatient.email !== patient.email ||
      currentPatient.phone !== patient.phone
    ) {
      mergedByUuid.set(patient.uuid, patient);
      changed = true;
    }
  });

  return changed ? Array.from(mergedByUuid.values()) : existingPatients;
};

const toConversationPreviewText = (
  preview?: string | null,
  fallback?: string | null,
  lastMessageAt?: string | null,
): string => {
  const normalizedPreview = (preview || "").trim();
  if (normalizedPreview) {
    return normalizedPreview;
  }

  const normalizedFallback = (fallback || "").trim();
  if (normalizedFallback) {
    return normalizedFallback;
  }

  if ((lastMessageAt || "").trim()) {
    return "Media or non-text message";
  }

  return "No messages yet.";
};

export const toMessageConversationListItems = (
  conversations: TCommunicationsConversation[],
  patientNameByUuid: Map<string, string>,
  clinicOwnedNumbers?: Set<string>,
): TMessageConversationListItem[] =>
  conversations.map((conversation) => {
    const participantE164 = (conversation.participant_e164 || "").trim();
    const patientUuid = (conversation.patient_uuid || "").trim();
    const fromE164 = (conversation.from_e164 || "").trim();
    const toE164 = (conversation.to_e164 || "").trim();
    const clinicE164 = (conversation.clinic_e164 || "").trim();
    const assignedE164 = (conversation.assigned_e164 || "").trim();
    const clinicOwnedE164Candidates = [
      assignedE164,
      clinicE164,
      fromE164,
      toE164,
    ].filter(Boolean);
    const clinicOwnedE164 = !clinicOwnedNumbers?.size
      ? clinicOwnedE164Candidates[0] || ""
      : clinicOwnedE164Candidates.find((value) =>
          clinicOwnedNumbers.has(value),
        ) || "";
    const normalizedUnreadCount = Math.max(conversation.unread_count || 0, 0);
    const isRead =
      typeof conversation.is_read === "boolean"
        ? conversation.is_read
        : normalizedUnreadCount <= 0;
    const unreadCount = normalizedUnreadCount || (isRead ? 0 : 1);

    return {
      conversationUuid: conversation.conversation_uuid,
      patientUuid: conversation.patient_uuid,
      participantE164,
      clinicOwnedE164: clinicOwnedE164 || null,
      patientLabel: patientUuid
        ? patientNameByUuid.get(patientUuid) || "Unknown Patient"
        : null,
      lastMessagePreview: toConversationPreviewText(
        conversation.last_message_preview,
        conversation.last_message_body,
        conversation.last_message_at,
      ),
      lastMessageAt: conversation.last_message_at,
      unreadCount,
      isRead,
      readAt: conversation.read_at || null,
    };
  });

export const toVoiceNoteFromCallNote = (
  note: TCommunicationsCallNote,
  index: number,
): VoiceNote => ({
  id: String(note.id),
  filename:
    note.audio_info?.[0]?.s3_key ||
    note.audio_info?.[0]?.type ||
    `call-note-${note.id}-${index}`,
  transcription: "",
  note: note.note || "",
  type: note.encounter_type?.toLowerCase().replace(/\s+/g, "_") || "phone_call",
  date: note.date_of_recording || note.created_at || "",
});

export const toVoiceNoteFromPatientNote = (
  note: TDocumentationPatientNote,
  index: number,
): VoiceNote => ({
  id: String(note.id),
  filename:
    note.audio_info?.[0]?.s3_key ||
    note.audio_info?.[0]?.type ||
    `patient-note-${note.id}-${index}`,
  transcription: "",
  note: note.note || "",
  type: note.note_type?.toLowerCase().replace(/\s+/g, "_") || "phone_call",
  date: note.date_of_recording || note.created_at || "",
});
